import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, useForm } from '@inertiajs/react'
import { HelpCircle, MessageSquare, Megaphone, Save, X } from 'lucide-react'

interface Community {
  id: number
  name: string
  slug: string
}

interface NewProps {
  communities: Community[]
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800 border-blue-200'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-700 border-gray-200'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
  }
} as const

export default function New({ communities }: NewProps) {
  const { data, setData, post, processing, errors } = useForm({
    title: '',
    body: '',
    post_type: 'announcement' as 'question' | 'discussion' | 'announcement',
    // 'interpro' = publication visible Médecins ET Pharmaciens (espace partagé)
    community_id: 'interpro' as string
  })

  const isInterpro = data.community_id === 'interpro'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post('/admin/posts')
  }

  return (
    <AdminLayout
      title="Nouvelle publication"
      breadcrumbs={[
        { label: 'Publications', href: '/admin/posts' },
        { label: 'Nouvelle publication' }
      ]}
    >
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl">
          <Card className="p-6">
            {/* Communauté cible */}
            <div className="mb-6">
              <label htmlFor="community" className="block text-sm font-medium text-text-dark mb-2">
                Communauté
              </label>
              <Select
                id="community"
                value={data.community_id}
                onChange={(e) => setData('community_id', e.target.value)}
              >
                <option value="interpro">Passerelle Santé (médecins et pharmaciens)</option>
                {communities
                  .filter((c) => c.slug !== 'patients')
                  .map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
              </Select>
              <p className="mt-1 text-xs text-text-muted">
                {isInterpro
                  ? 'Publié dans l\'espace partagé, visible par les médecins et les pharmaciens.'
                  : 'Publié uniquement dans cette communauté.'}
              </p>
            </div>

            {/* Type de publication */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-dark mb-2">
                Type de publication
              </label>
              <div className="flex gap-3">
                {(['question', 'discussion', 'announcement'] as const).map((type) => {
                  const config = POST_TYPE_CONFIG[type]
                  const Icon = config.icon
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setData('post_type', type)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        data.post_type === type
                          ? `${config.className} border-current`
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={18} />
                      {config.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Titre */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-2">
                Titre
              </label>
              <Input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                placeholder="Titre de la publication"
                required
                minLength={5}
                maxLength={200}
              />
              {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title}</p>}
            </div>

            {/* Contenu */}
            <div className="mb-6">
              <label htmlFor="body" className="block text-sm font-medium text-text-dark mb-2">
                Contenu
              </label>
              <textarea
                id="body"
                value={data.body}
                onChange={(e) => setData('body', e.target.value)}
                placeholder="Contenu de la publication..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
                rows={10}
                required
                minLength={10}
              />
              {errors.body && <p className="text-sm text-red-600 mt-1">{errors.body}</p>}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <Button type="submit" disabled={processing} className="flex items-center gap-2">
                <Save size={16} />
                {processing ? 'Publication...' : 'Publier'}
              </Button>
              <Link href="/admin/posts">
                <Button type="button" variant="secondary" className="flex items-center gap-2">
                  <X size={16} />
                  Annuler
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </form>
    </AdminLayout>
  )
}
